// AdminUserManagement.tsx
// MongoDB stores complete shop details.
// PostgreSQL stores only shop reward / margin configuration.

import React, { useEffect, useState } from 'react';
import './Users.css';

import Sidebar from '../components/Sidebar';
import Buttons from '../components/Button';
import Loading from '../components/Loading';
import AlertMessage from '../components/AlertMessage';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../state/store';

import {
  setUsers,
  addUser,
  updateUser,
  setLoading as setUserLoading,
  setError as setUserError,
} from '../state/slices/userSlice';

import {
  setVendors,
  setLoading as setVendorLoading,
  setError as setVendorError,
} from '../state/slices/vendorSlice';

import {
  setShops,
  setLoading as setShopLoading,
  setError as setShopError,
} from '../state/slices/shopSlice';

import {
  fetchUsers,
  fetchVendorByUserId,
  fetchShopsByVendorId,
  createVendorApi,
  updateVendorApi,
  createShopApi,
  updateShopApi,

  // NEW
  saveShopRewardConfigApi,
} from '../graphql/api/userApi';

import UserManagement from './users/UserManagement';
import VendorManagement from './users/VendorManagement';
import ShopManagement from './users/ShopManagement';

import { CREATE_USER, UPDATE_USER } from '../graphql/userMutation';
import { useMutation } from '@apollo/client';

import { LocationInput } from '../interfaces/user';


// ============================================================
// TYPES
// ============================================================

interface UserForm {
  username: string;
  email: string;
  phoneNumber: string;
  role: string;
  status: string;
  password: string;
  confirmPassword: string;
  gender: string;
  id: string;
}

interface VendorForm {
  businessName: string;
  email: string;
  phone: string;
  address: string;
  panno: string;
  gstno: string;
  id: string;
}

interface ShopForm {
  id: string;
  vendorId: string;
  shopName: string;
  shopLogo: string;

  location: {
    city: string;
    state: string;
    country: string;
  };

  createdAt: string;
  fromDate: string;
  toDate: string;

  // NEW REWARD FIELDS
  shopCommPct: string;
  custPct: string;
  lngNetPct: string;
  isActive: boolean;
}


// ============================================================
// INITIAL VALUES
// ============================================================

const initialUserForm: UserForm = {
  username: '',
  email: '',
  phoneNumber: '',
  role: 'user',
  status: 'active',
  password: '',
  confirmPassword: '',
  gender: '',
  id: '',
};

const initialVendorForm: VendorForm = {
  businessName: '',
  email: '',
  phone: '',
  address: '',
  panno: '',
  gstno: '',
  id: '',
};

const initialShopForm: ShopForm = {
  id: '',
  vendorId: '',
  shopName: '',
  shopLogo: '',

  location: {
    city: '',
    state: '',
    country: '',
  },

  createdAt: new Date().toISOString(),
  fromDate: '',
  toDate: '',

  // NEW
  shopCommPct: '10',
  custPct: '5',
  lngNetPct: '5',
  isActive: true,
};


// ============================================================
// COMPONENT
// ============================================================

const AdminUserManagement: React.FC = () => {
  const dispatch = useDispatch();

  // ==========================================================
  // REDUX
  // ==========================================================

  const users = useSelector(
    (state: RootState) => state.user.users
  );

  const vendors = useSelector(
    (state: RootState) => state.vendor.vendors
  );

  const shops = useSelector(
    (state: RootState) => state.shop.shops
  );

  const isLoading = useSelector(
    (state: RootState) =>
      state.user.loading ||
      state.vendor.loading ||
      state.shop.loading
  );

  const error = useSelector(
    (state: RootState) =>
      state.user.error ||
      state.vendor.error ||
      state.shop.error
  );


  // ==========================================================
  // STATE
  // ==========================================================

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= 768
  );

  const [activeTab, setActiveTab] =
    useState<'user' | 'vendor' | 'shop'>('user');

  const [isEditing, setIsEditing] = useState(false);

  const [selections, setSelections] = useState({
    userId: '',
    vendorId: '',
  });

  const [validationErrors, setValidationErrors] =
    useState<Record<string, string>>({});

  const [userForm, setUserForm] =
    useState<UserForm>(initialUserForm);

  const [vendorForm, setVendorForm] =
    useState<VendorForm>(initialVendorForm);

  const [shopForm, setShopForm] =
    useState<ShopForm>(initialShopForm);

  const [passwordError, setPasswordError] =
    useState('');

  const [alertMessage, setAlertMessage] =
    useState('');

  const [alertType, setAlertType] =
    useState<'success' | 'error' | 'info' | 'warning'>('info');


  // ==========================================================
  // WINDOW RESIZE
  // ==========================================================

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  // ==========================================================
  // USER MUTATIONS
  // ==========================================================

  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: (data) => {
      if (data?.createUser) {
        dispatch(addUser(data.createUser));
      }
    },
  });

  const [updateUserMutation] = useMutation(UPDATE_USER, {
    onCompleted: (data) => {
      if (data?.updateUser) {
        dispatch(updateUser(data.updateUser));
      }
    },
  });


  // ==========================================================
  // ALERT AUTO CLEAR
  // ==========================================================

  useEffect(() => {
    if (!alertMessage) {
      return;
    }

    const timer = setTimeout(() => {
      setAlertMessage('');
    }, 3000);

    return () => clearTimeout(timer);
  }, [alertMessage]);


  // ==========================================================
  // VALIDATION
  // ==========================================================

  const validateUserForm = () => {
    const errors: Record<string, string> = {};

    if (!userForm.username.trim()) {
      errors.username = 'User Name is required';
    }

    if (!userForm.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone Number is required';
    } else if (
      !/^[6-9]\d{9}$/.test(userForm.phoneNumber)
    ) {
      errors.phoneNumber =
        'Enter valid 10 digit mobile number';
    }

    if (!userForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
        userForm.email
      )
    ) {
      errors.email = 'Invalid email address';
    }

    if (!isEditing) {
      if (!userForm.password) {
        errors.password = 'Password is required';
      } else if (userForm.password.length < 6) {
        errors.password =
          'Password must be minimum 6 characters';
      }

      if (
        userForm.confirmPassword !== userForm.password
      ) {
        errors.confirmPassword =
          'Passwords do not match';
      }
    }

    if (!userForm.role) {
      errors.role = 'Role is required';
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };


  const validateVendorForm = () => {
    const errors: Record<string, string> = {};

    if (!selections.userId) {
      errors.userId = 'Select User';
    }

    if (!vendorForm.businessName.trim()) {
      errors.businessName =
        'Vendor Name is required';
    }

    if (!vendorForm.phone.trim()) {
      errors.phone = 'Phone Number is required';
    } else if (
      !/^[6-9]\d{9}$/.test(vendorForm.phone)
    ) {
      errors.phone =
        'Enter valid mobile number';
    }

    if (!vendorForm.email.trim()) {
      errors.email = 'Email is required';
    }

    if (
      vendorForm.gstno &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        vendorForm.gstno
      )
    ) {
      errors.gstno = 'Invalid GST Number';
    }

    if (
      vendorForm.panno &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(
        vendorForm.panno
      )
    ) {
      errors.panno = 'Invalid PAN Number';
    }

    if (!vendorForm.address.trim()) {
      errors.address = 'Address is required';
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };


  // ==========================================================
  // SHOP VALIDATION
  // ==========================================================

  const validateShopForm = () => {
    const errors: Record<string, string> = {};

    if (!selections.userId) {
      errors.userId = 'Select User';
    }

    if (!selections.vendorId) {
      errors.vendorId = 'Select Vendor';
    }

    if (!shopForm.shopName.trim()) {
      errors.shopName = 'Shop Name is required';
    }

    if (!shopForm.location.city.trim()) {
      errors.city = 'City is required';
    }

    if (!shopForm.location.state.trim()) {
      errors.state = 'State is required';
    }

    if (!shopForm.location.country.trim()) {
      errors.country = 'Country is required';
    }

    if (!shopForm.fromDate) {
      errors.fromDate = 'From Date required';
    }

    if (!shopForm.toDate) {
      errors.toDate = 'To Date required';
    }

    if (
      shopForm.fromDate &&
      shopForm.toDate &&
      new Date(shopForm.toDate) <
        new Date(shopForm.fromDate)
    ) {
      errors.toDate =
        'To Date should be greater than From Date';
    }


    // ========================================================
    // REWARD VALIDATION
    // ========================================================

    const shopCommPct =
      Number(shopForm.shopCommPct);

    const custPct =
      Number(shopForm.custPct);

    if (
      shopForm.shopCommPct === '' ||
      Number.isNaN(shopCommPct)
    ) {
      errors.shopCommPct =
        'Shop Commission % is required';
    } else if (
      shopCommPct < 0 ||
      shopCommPct > 100
    ) {
      errors.shopCommPct =
        'Commission must be between 0 and 100';
    }

    if (
      shopForm.custPct === '' ||
      Number.isNaN(custPct)
    ) {
      errors.custPct =
        'Customer Reward % is required';
    } else if (
      custPct < 0 ||
      custPct > 100
    ) {
      errors.custPct =
        'Customer reward must be between 0 and 100';
    }

    if (custPct > shopCommPct) {
      errors.custPct =
        'Customer reward cannot be greater than shop commission';
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };


  // ==========================================================
  // FETCH USERS
  // ==========================================================

  useEffect(() => {
    const fetchUserData = async () => {
      dispatch(setUserLoading(true));

      try {
        const result = await fetchUsers();

        dispatch(setUsers(result));
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Error fetching users';

        dispatch(setUserError(message));
      } finally {
        dispatch(setUserLoading(false));
      }
    };

    fetchUserData();
  }, [dispatch]);


  // ==========================================================
  // FETCH VENDORS FOR USER
  // ==========================================================

  useEffect(() => {
    const fetchVendorData = async () => {
      if (
        !selections.userId ||
        (activeTab !== 'vendor' &&
          activeTab !== 'shop')
      ) {
        return;
      }

      dispatch(setVendorLoading(true));

      try {
        const result =
          await fetchVendorByUserId(
            selections.userId
          );

        dispatch(setVendors(result));
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Error fetching vendors';

        dispatch(setVendorError(message));
      } finally {
        dispatch(setVendorLoading(false));
      }
    };

    fetchVendorData();
  }, [
    selections.userId,
    activeTab,
    dispatch,
  ]);


  // ==========================================================
  // FETCH SHOPS FOR VENDOR
  // ==========================================================

  useEffect(() => {
    const fetchShopData = async () => {
      if (
        !selections.vendorId ||
        activeTab !== 'shop'
      ) {
        return;
      }

      dispatch(setShopLoading(true));

      try {
        const result =
          await fetchShopsByVendorId(
            selections.vendorId
          );

        dispatch(setShops(result));
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Error fetching shops';

        dispatch(setShopError(message));
      } finally {
        dispatch(setShopLoading(false));
      }
    };

    fetchShopData();
  }, [
    selections.vendorId,
    activeTab,
    dispatch,
  ]);


  // ==========================================================
  // RESET VENDOR WHEN USER CHANGES
  // ==========================================================

  useEffect(() => {
    if (selections.userId) {
      setSelections((previous) => ({
        ...previous,
        vendorId: '',
      }));
    }
  }, [selections.userId]);


  // ==========================================================
  // RESET FORMS
  // ==========================================================

  const resetForms = () => {
    setUserForm({
      ...initialUserForm,
    });

    setVendorForm({
      ...initialVendorForm,
    });

    setShopForm({
      ...initialShopForm,
      createdAt: new Date().toISOString(),
    });

    setValidationErrors({});
    setPasswordError('');
    setIsEditing(false);
  };


  // ==========================================================
  // INPUT HANDLERS
  // ==========================================================

  const handleUserChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement
    >
  ) => {
    const {
      name,
      value,
    } = e.target;

    setUserForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };


  const handleVendorChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement
    >
  ) => {
    const {
      name,
      value,
    } = e.target;

    setVendorForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };


  const handleShopChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement
    >
  ) => {
    const {
      name,
      value,
    } = e.target;

    setShopForm((previous) => ({
      ...previous,
      [name]: value,
    }));


    // ========================================================
    // AUTO CALCULATE LNG NET %
    // ========================================================

    if (
      name === 'shopCommPct' ||
      name === 'custPct'
    ) {
      const shopCommission =
        name === 'shopCommPct'
          ? Number(value)
          : Number(shopForm.shopCommPct);

      const customerReward =
        name === 'custPct'
          ? Number(value)
          : Number(shopForm.custPct);

      if (
        !Number.isNaN(shopCommission) &&
        !Number.isNaN(customerReward)
      ) {
        const net =
          shopCommission - customerReward;

        setShopForm((previous) => ({
          ...previous,
          lngNetPct: Math.max(
            0,
            net
          ).toFixed(2),
        }));
      }
    }
  };


  const handleSelectionChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const {
      name,
      value,
    } = e.target;

    setSelections((previous) => ({
      ...previous,
      [name]: value,
    }));
  };


  // ==========================================================
  // USER SUBMIT
  // ==========================================================

  const handleUserSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    if (!validateUserForm()) {
      return;
    }

    if (
      !isEditing &&
      userForm.password !==
        userForm.confirmPassword
    ) {
      setPasswordError(
        'Passwords do not match'
      );

      return;
    }

    setPasswordError('');

    try {
      if (isEditing) {
        await updateUserMutation({
          variables: {
            id: userForm.id,
            username: userForm.username,
            email: userForm.email,
            phoneNumber:
              userForm.phoneNumber,
            role: userForm.role,
            status: userForm.status,
            gender: userForm.gender,
          },
        });
      } else {
        await createUser({
          variables: {
            username: userForm.username,
            email: userForm.email,
            phoneNumber:
              userForm.phoneNumber,
            role: userForm.role,
            status: userForm.status,
            password: userForm.password,
            gender: userForm.gender,
          },
        });
      }

      setAlertMessage(
        isEditing
          ? 'User updated successfully'
          : 'User created successfully'
      );

      setAlertType('success');

      setUserForm({
        ...initialUserForm,
      });

      setIsEditing(false);
    } catch (err: any) {
      console.error(
        'User mutation error:',
        err
      );

      setAlertMessage(
        err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          'An error occurred'
      );

      setAlertType('error');
    }
  };


  // ==========================================================
  // VENDOR SUBMIT
  // ==========================================================

  const handleVendorSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    if (!validateVendorForm()) {
      return;
    }

    const {
      id,
      businessName,
      email,
      phone,
      address,
      panno,
      gstno,
    } = vendorForm;

    const payload = {
      businessName,
      email,
      phone,
      address,
      panno,
      gstno,
    };

    try {
      if (isEditing && id) {
        await updateVendorApi(
          id,
          payload
        );

        setAlertMessage(
          'Vendor updated successfully'
        );
      } else {
        if (!selections.userId) {
          setAlertMessage(
            'Please select a user before creating a vendor.'
          );

          setAlertType('error');

          return;
        }

        await createVendorApi({
          ...payload,
          userId: selections.userId,
        });

        setAlertMessage(
          'Vendor created successfully'
        );
      }

      setAlertType('success');

      if (selections.userId) {
        const result =
          await fetchVendorByUserId(
            selections.userId
          );

        dispatch(setVendors(result));
      }

      resetForms();
    } catch (err) {
      console.error(
        'Vendor mutation error:',
        err
      );

      setAlertMessage(
        'An error occurred while submitting vendor'
      );

      setAlertType('error');
    }
  };


  // ==========================================================
  // EXTRACT MONGODB SHOP ID
  // ==========================================================

  const extractShopId = (
    response: any
  ): string => {
    if (!response) {
      return '';
    }

    // Direct:
    // { id: "xxx" }
    if (response.id) {
      return String(response.id);
    }

    // { shopId: "xxx" }
    if (response.shopId) {
      return String(response.shopId);
    }

    // { shop: { id: "xxx" } }
    if (response.shop?.id) {
      return String(
        response.shop.id
      );
    }

    // { data: { createShop: { id: "xxx" }}}
    if (
      response.data?.createShop?.id
    ) {
      return String(
        response.data.createShop.id
      );
    }

    // { data: { shop: { id: "xxx" }}}
    if (
      response.data?.shop?.id
    ) {
      return String(
        response.data.shop.id
      );
    }

    // { data: { shopId: "xxx" }}
    if (
      response.data?.shopId
    ) {
      return String(
        response.data.shopId
      );
    }

    return '';
  };


  // ==========================================================
  // SAVE SHOP REWARD CONFIG
  // ==========================================================

  const saveRewardConfiguration = async (
    shopId: string
  ) => {
    if (!shopId) {
      throw new Error(
        'MongoDB Shop ID was not returned after saving shop'
      );
    }

    const shopCommPct =
      Number(shopForm.shopCommPct);

    const custPct =
      Number(shopForm.custPct);

    if (
      Number.isNaN(shopCommPct) ||
      Number.isNaN(custPct)
    ) {
      throw new Error(
        'Invalid reward percentage'
      );
    }

    if (custPct > shopCommPct) {
      throw new Error(
        'Customer reward cannot be greater than shop commission'
      );
    }

    console.log(
      'Saving PostgreSQL reward configuration:',
      {
        shopId,
        shopCommPct,
        custPct,
        lngNetPct:
          shopCommPct - custPct,
      }
    );

    await saveShopRewardConfigApi(
      shopId,
      shopCommPct,
      custPct
    );
  };


  // ==========================================================
  // SHOP SUBMIT
  // ==========================================================

  const handleShopSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    if (!validateShopForm()) {
      return;
    }

    const {
      id,
      shopName,
      location,
      fromDate,
      toDate,
      shopLogo,
    } = shopForm;


    // ========================================================
    // SHOP IMAGE
    // ========================================================

    const fileInput =
      document.getElementById(
        'shopLogo'
      ) as HTMLInputElement;

    const shopLogoFile =
      fileInput?.files?.[0];


    // ========================================================
    // IMAGE REQUIRED ONLY FOR NEW SHOP
    // ========================================================

    if (
      !isEditing &&
      !shopLogoFile
    ) {
      setAlertMessage(
        'Image not found'
      );

      setAlertType('error');

      return;
    }


    // ========================================================
    // LOCATION
    // ========================================================

    const locationInput: LocationInput = {
      city:
        location.city ?? '',
      state:
        location.state ?? '',
      country:
        location.country ?? '',
    };


    // ========================================================
    // MONGODB SHOP PAYLOAD
    // ========================================================

    const mongoShopPayload: any = {
      shopName,
      vendorId:
        selections.vendorId,
      location: locationInput,
      fromDate,
      toDate,

      shopLogoFile:
        shopLogoFile,

      shopLogo:
        !shopLogoFile
          ? shopLogo
          : null,
    };


    try {

      // ======================================================
      // EDIT EXISTING SHOP
      // ======================================================

      if (
        isEditing &&
        id
      ) {

        const {
          vendorId,
          ...updatePayload
        } = mongoShopPayload;

        console.log(
          'Updating MongoDB shop:',
          id
        );

        const updateResponse =
          await updateShopApi(
            id,
            updatePayload
          );

        // Existing MongoDB ID
        const mongoShopId =
          id ||
          extractShopId(
            updateResponse
          );

        if (!mongoShopId) {
          throw new Error(
            'MongoDB Shop ID is missing'
          );
        }


        // ==============================================
        // SAVE / UPDATE POSTGRESQL CONFIG
        // ==============================================

        await saveRewardConfiguration(
          mongoShopId
        );

        setAlertMessage(
          'Shop and reward configuration updated successfully'
        );

      } else {

        // ==================================================
        // CREATE SHOP IN MONGODB FIRST
        // ==================================================

        console.log(
          'Creating shop in MongoDB...'
        );

        const createResponse =
          await createShopApi(
            mongoShopPayload
          );

        console.log(
          'MongoDB create shop response:',
          createResponse
        );


        // ==================================================
        // GET MONGODB SHOP ID
        // ==================================================

        const mongoShopId =
          extractShopId(
            createResponse
          );

        if (!mongoShopId) {
          throw new Error(
            'MongoDB Shop ID was not returned by createShopApi(). Cannot save reward configuration.'
          );
        }

        console.log(
          'MongoDB Shop ID:',
          mongoShopId
        );


        // ==================================================
        // SAVE REWARD CONFIGURATION IN POSTGRESQL
        // ==================================================

        await saveRewardConfiguration(
          mongoShopId
        );

        setAlertMessage(
          'Shop created and reward configuration saved successfully'
        );
      }


      // ======================================================
      // SUCCESS
      // ======================================================

      setAlertType('success');

      // Refresh shops
      if (
        selections.vendorId
      ) {
        const result =
          await fetchShopsByVendorId(
            selections.vendorId
          );

        dispatch(
          setShops(result)
        );
      }

      resetForms();

    } catch (err: any) {

      console.error(
        'Shop mutation error:',
        err
      );

      setAlertMessage(
        err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          'An error occurred while submitting shop'
      );

      setAlertType('error');
    }
  };


  // ==========================================================
  // BUTTON SUBMIT
  // ==========================================================

  const handleFormSubmit = (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    if (activeTab === 'user') {
      handleUserSubmit(e);
    } else if (
      activeTab === 'vendor'
    ) {
      handleVendorSubmit(e);
    } else {
      handleShopSubmit(e);
    }
  };


  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      style={{
        display: 'flex',
      }}
    >

      {/* =====================================================
          SIDEBAR
      ====================================================== */}

      <Sidebar
        isOpen={isSidebarOpen}
        setIsOpen={
          setIsSidebarOpen
        }
      />


      {/* =====================================================
          MAIN CONTENT
      ====================================================== */}

      <div
        style={{
          flex: 1,

          marginLeft:
            isSidebarOpen
              ? isMobile
                ? '0px'
                : '250px'
              : isMobile
              ? '0px'
              : '60px',

          transition:
            'margin-left 0.3s ease',

          padding: '20px',
        }}
      >

        <div
          className="admin-user-management"
        >

          <h2>
            User Management
          </h2>


          {/* =================================================
              ALERT
          ================================================== */}

          {alertMessage && (
            <AlertMessage
              message={
                alertMessage
              }
              type={
                alertType
              }
            />
          )}


          {/* =================================================
              LOADING
          ================================================== */}

          {isLoading && (
            <Loading />
          )}


          <br />
          <br />


          <div
            className="admin-container"
          >

            {/* =================================================
                FORM
            ================================================== */}

            <div
              className="admin-form"
            >

              {/* ===============================================
                  TABS
              ================================================ */}

              <div
                className="form-tabs"
              >

                <button
                  type="button"
                  className={
                    activeTab === 'user'
                      ? 'active-tab'
                      : ''
                  }
                  onClick={() =>
                    setActiveTab(
                      'user'
                    )
                  }
                >
                  User
                </button>

                <button
                  type="button"
                  className={
                    activeTab === 'vendor'
                      ? 'active-tab'
                      : ''
                  }
                  onClick={() =>
                    setActiveTab(
                      'vendor'
                    )
                  }
                >
                  Vendor
                </button>

                <button
                  type="button"
                  className={
                    activeTab === 'shop'
                      ? 'active-tab'
                      : ''
                  }
                  onClick={() =>
                    setActiveTab(
                      'shop'
                    )
                  }
                >
                  Shop
                </button>

              </div>


              {/* ===============================================
                  FORM
              ================================================ */}

              <form
                onSubmit={
                  handleFormSubmit
                }
              >

                {/* =================================================
                    USER FORM
                ================================================== */}

                {activeTab === 'user' && (
                  <>

                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="username"
                        >
                          User Name
                        </label>

                        <input
                          type="text"
                          id="username"
                          name="username"
                          value={
                            userForm.username
                          }
                          onChange={
                            handleUserChange
                          }
                          className="user_form-control"
                          required
                        />

                        {validationErrors.username && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.username
                            }
                          </small>
                        )}

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="phoneNumber"
                        >
                          Phone Number
                        </label>

                        <input
                          type="text"
                          id="phoneNumber"
                          name="phoneNumber"
                          value={
                            userForm.phoneNumber
                          }
                          onChange={
                            handleUserChange
                          }
                          className="user_form-control"
                          required
                        />

                        {validationErrors.phoneNumber && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.phoneNumber
                            }
                          </small>
                        )}

                      </div>

                    </div>


                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="email"
                        >
                          Email
                        </label>

                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={
                            userForm.email
                          }
                          onChange={
                            handleUserChange
                          }
                          className="user_form-control"
                          required
                        />

                        {validationErrors.email && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.email
                            }
                          </small>
                        )}

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="status"
                        >
                          Status
                        </label>

                        <select
                          id="status"
                          name="status"
                          value={
                            userForm.status
                          }
                          onChange={
                            handleUserChange
                          }
                          className="user_form-control"
                          required
                        >
                          <option value="active">
                            Active
                          </option>

                          <option value="inactive">
                            Inactive
                          </option>

                        </select>

                      </div>

                    </div>


                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="password"
                        >
                          Password
                        </label>

                        <input
                          type="password"
                          id="password"
                          name="password"
                          value={
                            userForm.password
                          }
                          onChange={
                            handleUserChange
                          }
                          className="user_form-control"
                          required={
                            !isEditing
                          }
                        />

                        {validationErrors.password && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.password
                            }
                          </small>
                        )}

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="confirmPassword"
                        >
                          Confirm Password
                        </label>

                        <input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={
                            userForm.confirmPassword
                          }
                          onChange={
                            handleUserChange
                          }
                          className="user_form-control"
                          required={
                            !isEditing
                          }
                        />

                        {validationErrors.confirmPassword && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.confirmPassword
                            }
                          </small>
                        )}

                      </div>

                    </div>


                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="gender"
                        >
                          Gender
                        </label>

                        <select
                          id="gender"
                          name="gender"
                          value={
                            userForm.gender
                          }
                          onChange={
                            handleUserChange
                          }
                          className="user_form-control"
                        >

                          <option value="">
                            Select Gender
                          </option>

                          <option value="male">
                            Male
                          </option>

                          <option value="female">
                            Female
                          </option>

                          <option value="other">
                            Other
                          </option>

                        </select>

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="role"
                        >
                          Role
                        </label>

                        <select
                          id="role"
                          name="role"
                          value={
                            userForm.role
                          }
                          onChange={
                            handleUserChange
                          }
                          className="user_form-control"
                          required
                        >

                          <option value="admin">
                            Admin
                          </option>

                          <option value="user">
                            Customer
                          </option>

                          <option value="vendor">
                            Vendor
                          </option>

                        </select>

                      </div>

                    </div>


                    {passwordError && (
                      <div
                        className="error"
                        style={{
                          color: 'red',
                        }}
                      >
                        {passwordError}
                      </div>
                    )}

                  </>
                )}


                {/* =================================================
                    VENDOR FORM
                ================================================== */}

                {activeTab === 'vendor' && (
                  <>

                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="vendorUserId"
                        >
                          Select User
                        </label>

                        <select
                          id="vendorUserId"
                          name="userId"
                          value={
                            selections.userId
                          }
                          onChange={
                            handleSelectionChange
                          }
                          className="user_form-control"
                          required
                        >

                          <option value="">
                            -- Select User --
                          </option>

                          {users
                            .filter(
                              (user) =>
                                [
                                  'vendor',
                                  'shopowner',
                                ].includes(
                                  user.role
                                )
                            )
                            .map(
                              (user) => (
                                <option
                                  key={
                                    user.id
                                  }
                                  value={
                                    user.id
                                  }
                                >
                                  {
                                    user.username
                                  }
                                </option>
                              )
                            )}

                        </select>

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="businessName"
                        >
                          Vendor Name
                        </label>

                        <input
                          type="text"
                          id="businessName"
                          name="businessName"
                          value={
                            vendorForm.businessName
                          }
                          onChange={
                            handleVendorChange
                          }
                          className="user_form-control"
                          required
                        />

                      </div>

                    </div>


                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="phone"
                        >
                          Phone
                        </label>

                        <input
                          type="text"
                          id="phone"
                          name="phone"
                          value={
                            vendorForm.phone
                          }
                          onChange={
                            handleVendorChange
                          }
                          className="user_form-control"
                          required
                        />

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="vendorEmail"
                        >
                          Email
                        </label>

                        <input
                          type="email"
                          id="vendorEmail"
                          name="email"
                          value={
                            vendorForm.email
                          }
                          onChange={
                            handleVendorChange
                          }
                          className="user_form-control"
                          required
                        />

                      </div>

                    </div>


                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="panno"
                        >
                          PAN No
                        </label>

                        <input
                          type="text"
                          id="panno"
                          name="panno"
                          value={
                            vendorForm.panno
                          }
                          onChange={
                            handleVendorChange
                          }
                          className="user_form-control"
                        />

                        {validationErrors.panno && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.panno
                            }
                          </small>
                        )}

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="gstno"
                        >
                          GST No
                        </label>

                        <input
                          type="text"
                          id="gstno"
                          name="gstno"
                          value={
                            vendorForm.gstno
                          }
                          onChange={
                            handleVendorChange
                          }
                          className="user_form-control"
                        />

                        {validationErrors.gstno && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.gstno
                            }
                          </small>
                        )}

                      </div>

                    </div>


                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="address"
                        >
                          Vendor Address
                        </label>

                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={
                            vendorForm.address
                          }
                          onChange={
                            handleVendorChange
                          }
                          className="user_form-control"
                          required
                        />

                      </div>

                    </div>

                  </>
                )}


                {/* =================================================
                    SHOP FORM
                ================================================== */}

                {activeTab === 'shop' && (
                  <>

                    {/* USER + VENDOR */}

                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="shopUserId"
                        >
                          Select User
                        </label>

                        <select
                          id="shopUserId"
                          name="userId"
                          value={
                            selections.userId
                          }
                          onChange={
                            handleSelectionChange
                          }
                          className="user_form-control"
                          required
                        >

                          <option value="">
                            -- Select User --
                          </option>

                          {users
                            .filter(
                              (user) =>
                                [
                                  'vendor',
                                  'shopowner',
                                ].includes(
                                  user.role
                                )
                            )
                            .map(
                              (user) => (
                                <option
                                  key={
                                    user.id
                                  }
                                  value={
                                    user.id
                                  }
                                >
                                  {
                                    user.username
                                  }
                                </option>
                              )
                            )}

                        </select>

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="vendorId"
                        >
                          Select Vendor
                        </label>

                        <select
                          id="vendorId"
                          name="vendorId"
                          value={
                            selections.vendorId
                          }
                          onChange={
                            handleSelectionChange
                          }
                          className="user_form-control"
                          required
                          disabled={
                            !selections.userId
                          }
                        >

                          <option value="">
                            -- Select Vendor --
                          </option>

                          {vendors
                            .filter(
                              (vendor) =>
                                vendor.userId ===
                                selections.userId
                            )
                            .map(
                              (vendor) => (
                                <option
                                  key={
                                    vendor.id
                                  }
                                  value={
                                    vendor.id
                                  }
                                >
                                  {
                                    vendor.businessName
                                  }
                                </option>
                              )
                            )}

                        </select>

                      </div>

                    </div>


                    {/* SHOP NAME + CITY */}

                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="shopName"
                        >
                          Shop Name
                        </label>

                        <input
                          type="text"
                          id="shopName"
                          name="shopName"
                          value={
                            shopForm.shopName
                          }
                          onChange={
                            handleShopChange
                          }
                          className="user_form-control"
                          required
                        />

                        {validationErrors.shopName && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.shopName
                            }
                          </small>
                        )}

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="city"
                        >
                          City
                        </label>

                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={
                            shopForm.location.city
                          }
                          onChange={(e) =>
                            setShopForm(
                              (previous) => ({
                                ...previous,

                                location: {
                                  ...previous.location,

                                  city:
                                    e.target
                                      .value,
                                },
                              })
                            )
                          }
                          className="user_form-control"
                          required
                        />

                        {validationErrors.city && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.city
                            }
                          </small>
                        )}

                      </div>

                    </div>


                    {/* STATE + COUNTRY */}

                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="state"
                        >
                          State
                        </label>

                        <input
                          type="text"
                          id="state"
                          name="state"
                          value={
                            shopForm.location.state
                          }
                          onChange={(e) =>
                            setShopForm(
                              (previous) => ({
                                ...previous,

                                location: {
                                  ...previous.location,

                                  state:
                                    e.target
                                      .value,
                                },
                              })
                            )
                          }
                          className="user_form-control"
                          required
                        />

                        {validationErrors.state && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.state
                            }
                          </small>
                        )}

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="country"
                        >
                          Country
                        </label>

                        <input
                          type="text"
                          id="country"
                          name="country"
                          value={
                            shopForm.location.country
                          }
                          onChange={(e) =>
                            setShopForm(
                              (previous) => ({
                                ...previous,

                                location: {
                                  ...previous.location,

                                  country:
                                    e.target
                                      .value,
                                },
                              })
                            )
                          }
                          className="user_form-control"
                          required
                        />

                        {validationErrors.country && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.country
                            }
                          </small>
                        )}

                      </div>

                    </div>


                    {/* FROM + TO */}

                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="fromDate"
                        >
                          From Date
                        </label>

                        <input
                          type="date"
                          id="fromDate"
                          name="fromDate"
                          value={
                            shopForm.fromDate
                          }
                          onChange={
                            handleShopChange
                          }
                          className="user_form-control"
                          required
                        />

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="toDate"
                        >
                          To Date
                        </label>

                        <input
                          type="date"
                          id="toDate"
                          name="toDate"
                          value={
                            shopForm.toDate
                          }
                          onChange={
                            handleShopChange
                          }
                          className="user_form-control"
                          required
                          min={
                            shopForm.fromDate
                          }
                        />

                      </div>

                    </div>


                    {/* =================================================
                        REWARD / MARGIN FIELDS
                    ================================================== */}

                    <div
                      style={{
                        marginTop: '20px',
                        marginBottom: '10px',
                      }}
                    >
                      <h4>
                        Reward / Margin Configuration
                      </h4>
                    </div>


                    <div className="form-row">

                      {/* SHOP COMMISSION */}

                      <div className="form-group">

                        <label
                          htmlFor="shopCommPct"
                        >
                          Shop Commission %
                        </label>

                        <input
                          type="number"
                          id="shopCommPct"
                          name="shopCommPct"
                          value={
                            shopForm.shopCommPct
                          }
                          onChange={
                            handleShopChange
                          }
                          className="user_form-control"
                          min="0"
                          max="100"
                          step="0.01"
                          required
                        />

                        {validationErrors.shopCommPct && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.shopCommPct
                            }
                          </small>
                        )}

                      </div>


                      {/* CUSTOMER REWARD */}

                      <div className="form-group">

                        <label
                          htmlFor="custPct"
                        >
                          Customer Reward %
                        </label>

                        <input
                          type="number"
                          id="custPct"
                          name="custPct"
                          value={
                            shopForm.custPct
                          }
                          onChange={
                            handleShopChange
                          }
                          className="user_form-control"
                          min="0"
                          max="100"
                          step="0.01"
                          required
                        />

                        {validationErrors.custPct && (
                          <small
                            style={{
                              color: 'red',
                            }}
                          >
                            {
                              validationErrors.custPct
                            }
                          </small>
                        )}

                      </div>

                    </div>


                    {/* LNG NET */}

                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="lngNetPct"
                        >
                          LNG Net %
                        </label>

                        <input
                          type="number"
                          id="lngNetPct"
                          name="lngNetPct"
                          value={
                            shopForm.lngNetPct
                          }
                          className="user_form-control"
                          readOnly
                          disabled
                        />

                        <small
                          style={{
                            color: '#666',
                          }}
                        >
                          LNG Net % = Shop
                          Commission % -
                          Customer Reward %
                        </small>

                      </div>


                      <div className="form-group">

                        <label
                          htmlFor="isActive"
                        >
                          Reward Configuration
                        </label>

                        <select
                          id="isActive"
                          name="isActive"
                          value={
                            shopForm.isActive
                              ? 'true'
                              : 'false'
                          }
                          onChange={(e) =>
                            setShopForm(
                              (previous) => ({
                                ...previous,

                                isActive:
                                  e.target
                                    .value ===
                                  'true',
                              })
                            )
                          }
                          className="user_form-control"
                        >

                          <option value="true">
                            Active
                          </option>

                          <option value="false">
                            Inactive
                          </option>

                        </select>

                      </div>

                    </div>


                    {/* SHOP IMAGE */}

                    <div className="form-row">

                      <div className="form-group">

                        <label
                          htmlFor="shopLogo"
                        >
                          Shop Image
                        </label>

                        <input
                          type="file"
                          id="shopLogo"
                          name="shopLogo"
                          accept="image/*"
                          onChange={(e) => {

                            const file =
                              e.target
                                .files?.[0];

                            if (!file) {
                              return;
                            }

                            setShopForm(
                              (previous) => ({
                                ...previous,

                                shopLogo:
                                  URL.createObjectURL(
                                    file
                                  ),
                              })
                            );

                          }}
                          className="user_form-control"
                        />

                      </div>

                    </div>

                  </>
                )}


                {/* =================================================
                    REDUX ERROR
                ================================================== */}

                {error && (
                  <div
                    className="error"
                    style={{
                      color: 'red',
                    }}
                  >
                    {error}
                  </div>
                )}


                {/* =================================================
                    FORM BUTTONS
                ================================================== */}

                <div
                  className="form-actions"
                >

                  <Buttons
                    text={
                      isEditing
                        ? 'Update'
                        : 'Add'
                    }
                    type="button"
                    variant={
                      isEditing
                        ? 'update'
                        : 'add'
                    }
                    className="ml-2"
                    onClick={
                      handleFormSubmit
                    }
                  />

                  <Buttons
                    text="Cancel"
                    type="button"
                    onClick={
                      resetForms
                    }
                    variant="cancel"
                    className="ml-2"
                  />

                </div>

              </form>

            </div>


            {/* =================================================
                TABLE
            ================================================== */}

            <div
              className="admin-table"
            >

              {activeTab === 'user' && (
                <UserManagement
                  isEditing={
                    isEditing
                  }
                  setIsEditing={
                    setIsEditing
                  }
                  setActiveTab={
                    setActiveTab
                  }
                  setUserForm={
                    setUserForm
                  }
                  users={users}
                  setUsers={(value) =>
                    dispatch(
                      setUsers(value)
                    )
                  }
                />
              )}


              {activeTab === 'vendor' && (
                <VendorManagement
                  users={users}
                  vendors={vendors}
                  setVendors={(value) =>
                    dispatch(
                      setVendors(value)
                    )
                  }
                  setActiveTab={
                    setActiveTab
                  }
                  setIsEditing={
                    setIsEditing
                  }
                  setVendorForm={
                    setVendorForm
                  }
                  setSelections={
                    setSelections
                  }
                />
              )}


              {activeTab === 'shop' && (
                <ShopManagement
                  vendors={vendors}
                  shops={shops}
                  setShops={(value) =>
                    dispatch(
                      setShops(value)
                    )
                  }
                  setActiveTab={
                    setActiveTab
                  }
                  setIsEditing={
                    setIsEditing
                  }
                  setShopForm={
                    setShopForm
                  }
                  setSelections={
                    setSelections
                  }
                />
              )}

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default AdminUserManagement;